import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl, SafeHtml } from '@angular/platform-browser';
import { firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/assets/pdfjs/pdf.worker.min.js';
import {
  StaffActivityApiService,
  ActivityFeedItem,
  ActivityMediaItem,
  LikeSlug,
  ActivityLikeEntry,
  ActivityComment,
  EditActivityFields,
} from '../services/staff-activity-api.service';
import { StaffSessionService } from '../services/staff-session.service';
import { ACTIVITY_REACTIONS, DEFAULT_REACTION_ICON } from '../constant/staff-activity.constant';

declare var customToastr: any;

// customToastr is a global loaded by the surrounding admin shell. If it's
// ever unavailable (e.g. not yet loaded), calling it directly throws and
// aborts whatever code runs after it — this guards every call site in the
// component so a missing toast library can never break post sync/UI state.
function safeToast(message: string, type: string) {
  try {
    if (typeof customToastr === 'function') {
      customToastr(message, type);
    }
  } catch {
    // Ignore — showing a toast should never be able to break the feature.
  }
}

@Component({
  selector: 'app-staff-class-activity',
  templateUrl: './staff-class-activity.component.html',
  styleUrls: ['../shared/staff-common.css', './staff-class-activity.component.css']
})
export class StaffClassActivityComponent implements OnInit {
  classId: string | null;
  staffId: string | number | null = null;
  isSuperAdmin = false;

  // Fine-grained action permissions — separate from the GET_ACTIVITY_TIMELINE
  // flag that gates the whole tile/route. Each mirrors a BluewingsFeatureName
  // entry in the same cached feature list, matching the mobile app's
  // ActivityData.js checkFeature() derivation exactly.
  createActivityEnabled = false;
  editActivityEnabled = false;
  deleteActivityEnabled = false;
  activityLikeEnabled = false;
  getCommentEnabled = false;
  createCommentEnabled = false;
  editCommentEnabled = false;
  deleteCommentEnabled = false;

  Math = Math;
  reactions = ACTIVITY_REACTIONS;
  defaultReactionIcon = DEFAULT_REACTION_ICON;

  posts: ActivityFeedItem[] = [];
  pageNumber = 1;
  maxPageNumber = 1;
  loading = true;
  loadingMore = false;
  loadError = false;

  // Sidebar "Share an update" composer state — for creating a new post.
  // Kept fully separate from the edit modal's state below so editing an
  // existing post never touches or clears an in-progress new-post draft.
  composerText = '';
  composerLevel: 'SCHOOL' | 'CLASS' = 'CLASS';
  composerFiles: File[] = [];
  submittingPost = false;

  // Edit post modal state — for editing an existing post.
  showComposer = false;
  editText = '';
  editFiles: File[] = [];
  editDeletedMediaIds: string[] = [];
  editingActivityId: number | null = null;
  submittingEdit = false;

  // Reaction picker state (which post's picker is open)
  openReactionPickerId: number | null = null;

  // Reaction list modal state
  showReactionList = false;
  reactionListActivityId: number | null = null;
  reactionListFilter: 'ALL' | LikeSlug = 'ALL';
  reactionStats: { like: number; love: number; laugh: number; celebration: number } = { like: 0, love: 0, laugh: 0, celebration: 0 };
  reactionEntries: ActivityLikeEntry[] = [];
  reactionListLoading = false;

  // Comment sheet state
  showComments = false;
  commentActivityId: number | null = null;
  comments: ActivityComment[] = [];
  commentsLoading = false;
  commentPageNumber = 1;
  commentHasMore = true;
  newComment = '';
  editingCommentId: string | null = null;
  editingCommentText = '';

  // Media lightbox state — images/videos/links all open in-app, never
  // navigate the user away from the staff portal.
  showLightbox = false;
  lightboxType: 'document' | 'link' = 'document';
  lightboxUrl: string | null = null;
  lightboxSafeUrl: SafeResourceUrl | null = null;

  // Gallery mode — opened from the collage, shows the full post (author,
  // time, text) plus every attached image/video stacked in a scrollable list.
  showGallery = false;
  galleryPost: ActivityFeedItem | null = null;
  galleryItems: ActivityMediaItem[] = [];

  // Full-screen preview of a single media item, opened from within the
  // gallery when a specific image/video is clicked.
  showMediaPreview = false;
  previewItem: ActivityMediaItem | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private staffActivityApi: StaffActivityApiService,
    private staffSession: StaffSessionService,
    private sanitizer: DomSanitizer,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.classId = this.route.snapshot.paramMap.get('id');
    const staffDetails = this.staffSession.getStaffDetails();
    this.staffId = staffDetails?.staffId ?? null;

    const schoolAndStaffInfo = this.staffSession.getSchoolAndStaffInfo();
    this.isSuperAdmin = !!schoolAndStaffInfo?.isSuperAdmin;

    this.createActivityEnabled = this.staffSession.isFeatureEnabled('ACTIVITY_TIMELINE');
    this.editActivityEnabled = this.staffSession.isFeatureEnabled('EDIT_ACTIVITY');
    this.deleteActivityEnabled = this.staffSession.isFeatureEnabled('DELETE_ACTIVITY');
    this.activityLikeEnabled = this.staffSession.isFeatureEnabled('ACTIVITY_STAFF_LIKE');
    this.getCommentEnabled = this.staffSession.isFeatureEnabled('ACTIVITY_STAFF_COMMENT_GET');
    this.createCommentEnabled = this.staffSession.isFeatureEnabled('ACTIVITY_COMMENT_CREATE');
    this.editCommentEnabled = this.staffSession.isFeatureEnabled('ACTIVITY_COMMENT_EDIT');
    this.deleteCommentEnabled = this.staffSession.isFeatureEnabled('ACTIVITY_STAFF_COMMENT_DELETE');

    this.loadFeed(1);
  }

  goBack() {
    this.router.navigate(['/staff/class', this.classId]);
  }

  private ids(): { staffId: string; classId: string } | null {
    if (!this.staffId || !this.classId) { return null; }
    return { staffId: String(this.staffId), classId: this.classId };
  }

  async loadFeed(page: number) {
    const ids = this.ids();
    if (!ids) {
      this.loading = false;
      this.loadError = true;
      return;
    }
    if (page === 1) {
      this.loading = true;
    } else {
      this.loadingMore = true;
    }
    this.loadError = false;
    try {
      const response = await firstValueFrom(this.staffActivityApi.getActivityData(ids.staffId, ids.classId, page));
      const responseDetails = response as any;
      if (responseDetails.status && responseDetails.body) {
        const activities: ActivityFeedItem[] = responseDetails.body.activities || [];
        this.posts = page === 1 ? activities : [...this.posts, ...activities];
        this.maxPageNumber = responseDetails.body.maxPageNumber || 1;
        this.pageNumber = page;
      } else {
        this.loadError = true;
      }
    } catch (error) {
      this.loadError = true;
    } finally {
      this.loading = false;
      this.loadingMore = false;
    }
  }

  loadMore() {
    if (this.pageNumber < this.maxPageNumber && !this.loadingMore) {
      this.loadFeed(this.pageNumber + 1);
    }
  }

  postAuthor(post: ActivityFeedItem): string {
    return post.createdByUserName || this.translate.instant('staff');
  }

  postAuthorImage(post: ActivityFeedItem): string {
    const img = post.createdByUserImage || '';
    return img && !img.startsWith('/images/') ? img : '';
  }

  postTime(post: ActivityFeedItem): string {
    return post.CreatedOn ? new Date(post.CreatedOn).toLocaleString() : '';
  }

  postBody(post: ActivityFeedItem): string {
    return post.activityDescription || '';
  }

  postImages(post: ActivityFeedItem): ActivityMediaItem[] {
    return post.media?.images || [];
  }

  postVideos(post: ActivityFeedItem): ActivityMediaItem[] {
    return post.media?.videos || [];
  }

  postDocuments(post: ActivityFeedItem): ActivityMediaItem[] {
    return post.media?.documents || [];
  }

  // mediaUrl -> data URL of the rendered first page, or null while pending/failed.
  private pdfThumbCache = new Map<string, string | null>();

  isPdfDocument(doc: ActivityMediaItem): boolean {
    return /\.pdf(\?|$)/i.test(doc.mediaUrl) || /pdf/i.test(doc.mediaSubType || '');
  }

  pdfThumbnail(mediaUrl: string): string | null {
    if (!this.pdfThumbCache.has(mediaUrl)) {
      this.pdfThumbCache.set(mediaUrl, null);
      this.renderPdfThumbnail(mediaUrl);
    }
    return this.pdfThumbCache.get(mediaUrl) || null;
  }

  private async renderPdfThumbnail(mediaUrl: string) {
    try {
      const pdf = await pdfjsLib.getDocument(mediaUrl).promise;
      const page = await pdf.getPage(1);
      const viewport = page.getViewport({ scale: 0.6 });
      const canvas = document.createElement('canvas');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const context = canvas.getContext('2d');
      if (!context) { return; }
      await page.render({ canvasContext: context, viewport }).promise;
      this.pdfThumbCache.set(mediaUrl, canvas.toDataURL('image/png'));
    } catch {
      // Leave the cache entry as null — falls back to the generic file chip.
    }
  }

  hasMedia(post: ActivityFeedItem): boolean {
    return this.postImages(post).length > 0 || this.postVideos(post).length > 0 || this.postDocuments(post).length > 0;
  }

  // Collage grid styling only kicks in when there's more than one visual
  // media item — a single image/video renders full-width instead.
  isCollage(post: ActivityFeedItem): boolean {
    return (this.postImages(post).length + this.postVideos(post).length) > 1;
  }

  visualMediaCount(post: ActivityFeedItem): number {
    return this.postImages(post).length + this.postVideos(post).length;
  }

  // All images+videos for a post, in display order — the collage tiles show
  // at most the first 4 of these, and the gallery browses the full list.
  allVisualMedia(post: ActivityFeedItem): ActivityMediaItem[] {
    return [...this.postImages(post), ...this.postVideos(post)];
  }

  // Every attachment on a post, including documents — used by the edit
  // modal so nothing attached to the post is hidden while editing.
  allMedia(post: ActivityFeedItem): ActivityMediaItem[] {
    return [...this.postImages(post), ...this.postVideos(post), ...this.postDocuments(post)];
  }

  // Number hidden behind the "+N" overlay on the last visible collage tile.
  extraMediaCount(post: ActivityFeedItem): number {
    return Math.max(0, this.visualMediaCount(post) - 4);
  }

  openGallery(post: ActivityFeedItem) {
    this.galleryPost = post;
    this.galleryItems = this.allVisualMedia(post);
    this.showGallery = true;
  }

  closeGallery() {
    this.showGallery = false;
    this.galleryPost = null;
  }

  openMediaPreview(item: ActivityMediaItem, event?: Event) {
    event?.stopPropagation();
    this.previewItem = item;
    this.showMediaPreview = true;
  }

  closeMediaPreview() {
    this.showMediaPreview = false;
    this.previewItem = null;
  }

  // Emoji for every distinct reaction type present on the post (e.g. both
  // ❤️ and 🎉 if different people reacted differently), shown stacked
  // before the "X and N others reacted" summary text.
  reactionIcons(post: ActivityFeedItem): string[] {
    const types = post.uniqueFeedLikeTypes || [];
    return this.reactions.filter(r => types.includes(r.slug as string)).map(r => r.icon);
  }

  reactionSummaryText(post: ActivityFeedItem): string {
    const count = this.postTotalLikes(post);
    if (!count) { return ''; }
    const lastName = post.lastLikedByUserName;
    if (!lastName) { return `${count} reaction${count === 1 ? '' : 's'}`; }
    const others = count - 1;
    if (others <= 0) { return `${lastName} reacted`; }
    return `${lastName} and ${others} other${others === 1 ? '' : 's'} reacted`;
  }

  // Detects a bare URL inside the post text so it can be shown as an
  // in-app-only link preview instead of raw text with a real <a> navigation.
  private readonly urlPattern = /(https?:\/\/[^\s]+)/i;
  private readonly youtubePattern = /(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{6,})/i;

  postLink(post: ActivityFeedItem): string | null {
    const match = (post.activityDescription || '').match(this.urlPattern);
    return match ? match[0] : null;
  }

  postTextWithoutLink(post: ActivityFeedItem): string {
    const link = this.postLink(post);
    return link ? this.postBody(post).replace(link, '').trim() : this.postBody(post);
  }

  private readonly hashtagPattern = /(^|\s)(#[a-zA-Z0-9_]+)/g;

  // Escapes the raw text first (so nothing in activityDescription can inject
  // markup), then wraps #hashtags in <strong> for bold rendering.
  postBodyHtml(post: ActivityFeedItem): SafeHtml {
    const escaped = this.escapeHtml(this.postTextWithoutLink(post));
    const bolded = escaped.replace(this.hashtagPattern, (_match, lead, tag) => `${lead}<strong>${tag}</strong>`);
    return this.sanitizer.bypassSecurityTrustHtml(bolded);
  }

  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  // Real thumbnail via YouTube's public static thumbnail URL pattern — no
  // API key or metadata fetch needed. Other links fall back to a generic
  // domain-only card since there's no OpenGraph source available client-side.
  linkPreview(url: string): { isYouTube: boolean; thumbnail: string; domain: string } {
    const ytMatch = url.match(this.youtubePattern);
    const domain = this.extractDomain(url);
    if (ytMatch) {
      return { isYouTube: true, thumbnail: `https://img.youtube.com/vi/${ytMatch[1]}/hqdefault.jpg`, domain };
    }
    return { isYouTube: false, thumbnail: '', domain };
  }

  private extractDomain(url: string): string {
    try {
      return new URL(url).hostname.replace(/^www\./, '');
    } catch {
      return url;
    }
  }

  postCommentCount(post: ActivityFeedItem): number {
    return post.commentsCount ?? 0;
  }

  postTotalLikes(post: ActivityFeedItem): number {
    return post.likesCount ?? 0;
  }

  isReactedByMe(post: ActivityFeedItem, slug: LikeSlug): boolean {
    return post.userLikedStatus === slug;
  }

  isAnyReactedByMe(post: ActivityFeedItem): boolean {
    return post.userLikedStatus !== null;
  }

  // Emoji of the user's own reaction, if any — used on the React button so
  // it reflects what they picked instead of always showing the thumb icon.
  myReactionIcon(post: ActivityFeedItem): string | null {
    const reaction = this.reactions.find(r => r.slug === post.userLikedStatus);
    return reaction ? reaction.icon : null;
  }

  // ---------- Inline composer (sidebar, create only) ----------
  // Object URLs for previewing newly selected (not-yet-uploaded) files.
  composerFilePreviews: string[] = [];
  isDraggingOverComposer = false;

  private setComposerFiles(files: File[]) {
    this.composerFilePreviews.forEach(url => URL.revokeObjectURL(url));
    this.composerFiles = files;
    this.composerFilePreviews = files.map(f => URL.createObjectURL(f));
  }

  onFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.setComposerFiles(Array.from(input.files));
    }
  }

  onComposerFilesDropped(event: DragEvent) {
    event.preventDefault();
    this.isDraggingOverComposer = false;
    const files = event.dataTransfer?.files;
    if (files && files.length) {
      this.setComposerFiles(Array.from(files));
    }
  }

  onComposerDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDraggingOverComposer = true;
  }

  onComposerDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDraggingOverComposer = false;
  }

  removeSelectedFile(index: number) {
    URL.revokeObjectURL(this.composerFilePreviews[index]);
    this.composerFiles.splice(index, 1);
    this.composerFilePreviews.splice(index, 1);
  }

  async submitInlinePost() {
    if (!this.createActivityEnabled) { return; }
    const ids = this.ids();
    if (!ids || !this.composerText.trim()) {
      safeToast(await firstValueFrom(this.translate.get('pleaseEnterAnUpdate')), 'error');
      return;
    }
    this.submittingPost = true;
    try {
      // Only a super admin may actually post school-wide — force class-level
      // for everyone else regardless of any stale/manipulated local state.
      const activityLevel = this.isSuperAdmin ? this.composerLevel : 'CLASS';
      const response = await firstValueFrom(this.staffActivityApi.createActivity({
        activityDescription: this.composerText.trim(),
        lang: 'en',
        classId: ids.classId,
        staffId: ids.staffId,
        activityLevel,
      }, this.composerFiles));
      const responseDetails = response as any;
      if (responseDetails.status) {
        safeToast(responseDetails.body?.message || await firstValueFrom(this.translate.get('staffActivityPosted')), 'success');
        this.composerText = '';
        this.composerFilePreviews.forEach(url => URL.revokeObjectURL(url));
        this.composerFiles = [];
        this.composerFilePreviews = [];
        this.loadFeed(1);
      } else {
        safeToast(responseDetails.message || await firstValueFrom(this.translate.get('somethingWentWrong')), 'error');
      }
    } catch (error) {
      safeToast(await firstValueFrom(this.translate.get('staffSomethingWentWrong')), 'error');
    } finally {
      this.submittingPost = false;
    }
  }

  // ---------- Edit modal ----------
  // Existing media on the post being edited, minus anything the user has
  // marked for removal — shown in the edit modal alongside newly added files.
  editingExistingMedia: ActivityMediaItem[] = [];

  openEditComposer(post: ActivityFeedItem) {
    if (!this.canEditPost(post)) { return; }
    this.editText = post.activityDescription || '';
    this.editFilePreviews.forEach(url => URL.revokeObjectURL(url));
    this.editFiles = [];
    this.editFilePreviews = [];
    this.editDeletedMediaIds = [];
    this.editingActivityId = post.activityId;
    this.editingExistingMedia = this.allMedia(post);
    this.showComposer = true;
  }

  closeComposer() {
    this.showComposer = false;
    this.editFilePreviews.forEach(url => URL.revokeObjectURL(url));
    this.editFilePreviews = [];
  }

  // Object URLs for previewing newly selected (not-yet-uploaded) edit files.
  editFilePreviews: string[] = [];
  isDraggingOverEdit = false;

  private setEditFiles(files: File[]) {
    this.editFilePreviews.forEach(url => URL.revokeObjectURL(url));
    this.editFiles = files;
    this.editFilePreviews = files.map(f => URL.createObjectURL(f));
  }

  onEditFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.setEditFiles(Array.from(input.files));
    }
  }

  onEditFilesDropped(event: DragEvent) {
    event.preventDefault();
    this.isDraggingOverEdit = false;
    const files = event.dataTransfer?.files;
    if (files && files.length) {
      this.setEditFiles(Array.from(files));
    }
  }

  onEditDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDraggingOverEdit = true;
  }

  onEditDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDraggingOverEdit = false;
  }

  removeEditFile(index: number) {
    URL.revokeObjectURL(this.editFilePreviews[index]);
    this.editFiles.splice(index, 1);
    this.editFilePreviews.splice(index, 1);
  }

  removeExistingMedia(item: ActivityMediaItem) {
    this.editingExistingMedia = this.editingExistingMedia.filter(m => m.mediaId !== item.mediaId);
    this.editDeletedMediaIds.push(item.mediaId);
  }

  async submitPost() {
    const ids = this.ids();
    if (!ids || !this.editText.trim() || !this.editingActivityId) {
      safeToast(await firstValueFrom(this.translate.get('pleaseEnterADescription')), 'error');
      return;
    }
    const editingPost = this.posts.find(p => p.activityId === this.editingActivityId);
    if (!editingPost || !this.canEditPost(editingPost)) { return; }

    this.submittingEdit = true;
    try {
      const editFields: EditActivityFields = {
        activityDescription: this.editText.trim(),
        lang: 'en',
        classId: ids.classId,
        staffId: ids.staffId,
        activityId: String(this.editingActivityId),
      };
      if (this.editDeletedMediaIds.length) {
        editFields.deletedMediaIdList = this.editDeletedMediaIds.join(',');
      }
      const response = await firstValueFrom(this.staffActivityApi.editActivity(editFields, this.editFiles));
      const responseDetails = response as any;
      if (responseDetails.status) {
        safeToast(responseDetails.body?.message || await firstValueFrom(this.translate.get('activityUpdated')), 'success');
        const editedPost = this.posts.find(p => p.activityId === this.editingActivityId);
        if (editedPost) {
          editedPost.activityDescription = this.editText.trim();
          editedPost.LastUpdatedOn = new Date().toISOString();
          const removedIds = new Set(this.editDeletedMediaIds);
          const newImages: ActivityMediaItem[] = [];
          this.editFiles.forEach((file, i) => {
            if (file.type.startsWith('image/')) {
              newImages.push({
                mediaId: `local-${Date.now()}-${i}`,
                mediaUrl: this.editFilePreviews[i],
                mediaType: 'IMAGE',
                mediaSubType: file.type.split('/')[1] || '',
              });
            }
          });
          editedPost.media = {
            images: [...editedPost.media.images.filter(m => !removedIds.has(m.mediaId)), ...newImages],
            videos: editedPost.media.videos.filter(m => !removedIds.has(m.mediaId)),
            documents: editedPost.media.documents.filter(m => !removedIds.has(m.mediaId)),
          };
        }
        this.editFilePreviews = [];
        this.showComposer = false;
      } else {
        safeToast(responseDetails.message || await firstValueFrom(this.translate.get('somethingWentWrong')), 'error');
      }
    } catch (error) {
      safeToast(await firstValueFrom(this.translate.get('staffSomethingWentWrong')), 'error');
    } finally {
      this.submittingEdit = false;
    }
  }

  isOwnPost(post: ActivityFeedItem): boolean {
    return this.staffId != null && String(post.createdByUserId) === String(this.staffId);
  }

  // Own posts: edit/delete gated by feature flag AND ownership.
  // Someone else's post: only a super admin may act on it, and only to
  // delete — edit is never available for another staff member's post,
  // regardless of the EDIT_ACTIVITY flag. Mirrors ActivityData.js's two
  // separate ActionMenu render branches exactly.
  canEditPost(post: ActivityFeedItem): boolean {
    return this.editActivityEnabled && this.isOwnPost(post);
  }

  canDeletePost(post: ActivityFeedItem): boolean {
    if (!this.deleteActivityEnabled) { return false; }
    return this.isOwnPost(post) || (this.isSuperAdmin && !this.isOwnPost(post));
  }

  isOwnComment(comment: ActivityComment): boolean {
    return this.staffId != null && String(comment.commentByUserId) === String(this.staffId);
  }

  // Comment edit has no super-admin override — strictly own-comment-only,
  // unlike post edit/delete and comment delete.
  canEditComment(comment: ActivityComment): boolean {
    return this.editCommentEnabled && this.isOwnComment(comment);
  }

  canDeleteComment(comment: ActivityComment): boolean {
    if (!this.deleteCommentEnabled) { return false; }
    return this.isOwnComment(comment) || this.isSuperAdmin;
  }

  // Delete-confirm modal state — shown when deleting your own post.
  showDeleteConfirmModal = false;
  deletingPost = false;
  private deleteConfirmTarget: ActivityFeedItem | null = null;

  // Delete-reason modal state — shown when deleting someone else's post.
  showDeleteReasonModal = false;
  deleteReasonText = '';
  private deleteReasonTarget: ActivityFeedItem | null = null;

  deletePost(post: ActivityFeedItem) {
    if (!this.canDeletePost(post)) { return; }
    if (this.isOwnPost(post)) {
      this.deleteConfirmTarget = post;
      this.showDeleteConfirmModal = true;
      return;
    }
    this.deleteReasonTarget = post;
    this.deleteReasonText = '';
    this.showDeleteReasonModal = true;
  }

  closeDeleteConfirmModal() {
    if (this.deletingPost) { return; }
    this.showDeleteConfirmModal = false;
    this.deleteConfirmTarget = null;
  }

  async confirmDelete() {
    if (!this.deleteConfirmTarget || this.deletingPost) { return; }
    const post = this.deleteConfirmTarget;
    await this.performDelete(post, null);
    this.showDeleteConfirmModal = false;
    this.deleteConfirmTarget = null;
  }

  closeDeleteReasonModal() {
    if (this.deletingPost) { return; }
    this.showDeleteReasonModal = false;
    this.deleteReasonTarget = null;
    this.deleteReasonText = '';
  }

  async confirmDeleteWithReason() {
    const reason = this.deleteReasonText.trim();
    if (!reason || !this.deleteReasonTarget || this.deletingPost) { return; }
    const post = this.deleteReasonTarget;
    await this.performDelete(post, reason);
    this.showDeleteReasonModal = false;
    this.deleteReasonTarget = null;
    this.deleteReasonText = '';
  }

  private async performDelete(post: ActivityFeedItem, reason: string | null) {
    const ids = this.ids();
    if (!ids) { return; }
    this.deletingPost = true;
    try {
      const response = await firstValueFrom(this.staffActivityApi.deleteActivity({
        lang: 'en',
        classId: ids.classId,
        staffId: ids.staffId,
        activityId: String(post.activityId),
        reason,
      }));
      const responseDetails = response as any;
      if (responseDetails.status) {
        safeToast(responseDetails.body?.message || await firstValueFrom(this.translate.get('activityDeleted')), 'success');
        this.posts = this.posts.filter(p => p.activityId !== post.activityId);
      } else {
        safeToast(responseDetails.message || await firstValueFrom(this.translate.get('somethingWentWrong')), 'error');
      }
    } catch (error) {
      safeToast(await firstValueFrom(this.translate.get('staffSomethingWentWrong')), 'error');
    } finally {
      this.deletingPost = false;
    }
  }

  // ---------- Likes / reactions ----------
  toggleReactionPicker(post: ActivityFeedItem) {
    this.openReactionPickerId = this.openReactionPickerId === post.activityId ? null : post.activityId;
  }

  async reactToPost(post: ActivityFeedItem, slug: LikeSlug) {
    if (!this.activityLikeEnabled) { return; }
    const ids = this.ids();
    if (!ids) { return; }
    this.openReactionPickerId = null;
    const alreadyReacted = this.isReactedByMe(post, slug);
    const newSlug: LikeSlug = alreadyReacted ? null : slug;
    const wasLiked = post.userLikedStatus !== null;
    try {
      const response = await firstValueFrom(this.staffActivityApi.likePost({
        lang: 'en',
        classId: ids.classId,
        staffId: ids.staffId,
        activityId: String(post.activityId),
        likeSlug: newSlug,
      }));
      const responseDetails = response as any;
      if (responseDetails.status && responseDetails.body) {
        post.uniqueFeedLikeTypes = responseDetails.body.uniqueLikeTypes || [];
        post.userLikedStatus = newSlug;
        const nowLiked = newSlug !== null;
        if (wasLiked && !nowLiked) {
          post.likesCount = Math.max(post.likesCount - 1, 0);
        } else if (!wasLiked && nowLiked) {
          post.likesCount = post.likesCount + 1;
        }
      } else {
        safeToast(responseDetails.message || await firstValueFrom(this.translate.get('somethingWentWrong')), 'error');
      }
    } catch (error) {
      safeToast(await firstValueFrom(this.translate.get('staffSomethingWentWrong')), 'error');
    }
  }

  async openReactionList(post: ActivityFeedItem) {
    const ids = this.ids();
    if (!ids) { return; }
    this.reactionListActivityId = post.activityId;
    this.reactionListFilter = 'ALL';
    this.showReactionList = true;
    await this.loadReactionList(1);
  }

  async loadReactionList(page: number) {
    const ids = this.ids();
    if (!ids || !this.reactionListActivityId) { return; }
    this.reactionListLoading = true;
    try {
      const response = await firstValueFrom(
        this.staffActivityApi.getReactions(ids.staffId, ids.classId, String(this.reactionListActivityId), this.reactionListFilter, page)
      );
      const responseDetails = response as any;
      if (responseDetails.status && responseDetails.body) {
        this.reactionStats = responseDetails.body.activityLikesStat;
        this.reactionEntries = page === 1 ? responseDetails.body.likes : [...this.reactionEntries, ...responseDetails.body.likes];
      }
    } catch (error) {
      safeToast(await firstValueFrom(this.translate.get('couldNotLoadReactions')), 'error');
    } finally {
      this.reactionListLoading = false;
    }
  }

  setReactionFilter(filter: 'ALL' | LikeSlug) {
    this.reactionListFilter = filter;
    this.loadReactionList(1);
  }

  closeReactionList() {
    this.showReactionList = false;
  }

  // ---------- Comments ----------
  async openCommentSheet(post: ActivityFeedItem) {
    this.commentActivityId = post.activityId;
    this.comments = [];
    this.commentPageNumber = 1;
    this.commentHasMore = true;
    this.newComment = '';
    this.showComments = true;
    await this.loadComments(1);
  }

  async loadComments(page: number) {
    if (!this.getCommentEnabled) { return; }
    const ids = this.ids();
    if (!ids || !this.commentActivityId) { return; }
    this.commentsLoading = true;
    try {
      const response = await firstValueFrom(this.staffActivityApi.getActivityComment(ids.staffId, ids.classId, String(this.commentActivityId), page));
      const responseDetails = response as any;
      if (responseDetails.status && responseDetails.body) {
        const newComments: ActivityComment[] = responseDetails.body.activityComments || [];
        this.comments = page === 1 ? newComments : [...this.comments, ...newComments];
        this.commentPageNumber = page;
        this.commentHasMore = newComments.length >= 10;
      }
    } catch (error) {
      safeToast(await firstValueFrom(this.translate.get('couldNotLoadComments')), 'error');
    } finally {
      this.commentsLoading = false;
    }
  }

  loadMoreComments() {
    if (this.commentHasMore && !this.commentsLoading) {
      this.loadComments(this.commentPageNumber + 1);
    }
  }

  closeCommentSheet() {
    this.showComments = false;
    this.editingCommentId = null;
  }

  async submitComment() {
    if (!this.createCommentEnabled) { return; }
    const ids = this.ids();
    if (!ids || !this.commentActivityId || !this.newComment.trim()) { return; }
    try {
      const response = await firstValueFrom(this.staffActivityApi.doComment({
        lang: 'en',
        classId: ids.classId,
        staffId: ids.staffId,
        activityId: String(this.commentActivityId),
        comment: this.newComment.trim(),
      }));
      const responseDetails = response as any;
      if (responseDetails.status) {
        this.newComment = '';
        this.loadComments(1);
        const post = this.posts.find(p => p.activityId === this.commentActivityId);
        if (post) {
          post.commentsCount = (post.commentsCount ?? 0) + 1;
        }
      } else {
        safeToast(responseDetails.message || await firstValueFrom(this.translate.get('somethingWentWrong')), 'error');
      }
    } catch (error) {
      safeToast(await firstValueFrom(this.translate.get('staffSomethingWentWrong')), 'error');
    }
  }

  startEditComment(comment: ActivityComment) {
    if (!this.canEditComment(comment)) { return; }
    this.editingCommentId = comment.commentId;
    this.editingCommentText = comment.comment;
  }

  cancelEditComment() {
    this.editingCommentId = null;
    this.editingCommentText = '';
  }

  async saveEditedComment() {
    const ids = this.ids();
    if (!ids || !this.editingCommentId || !this.editingCommentText.trim()) { return; }
    const editingComment = this.comments.find(c => c.commentId === this.editingCommentId);
    if (!editingComment || !this.canEditComment(editingComment)) { return; }
    try {
      const response = await firstValueFrom(this.staffActivityApi.editActivityComment({
        lang: 'en',
        classId: ids.classId,
        staffId: ids.staffId,
        comment: this.editingCommentText.trim(),
        commentId: this.editingCommentId,
      }));
      const responseDetails = response as any;
      if (responseDetails.status) {
        const comment = this.comments.find(c => c.commentId === this.editingCommentId);
        if (comment) {
          comment.comment = this.editingCommentText.trim();
        }
        this.cancelEditComment();
      } else {
        safeToast(await firstValueFrom(this.translate.get('somethingWentWrong')), 'error');
      }
    } catch (error) {
      safeToast(await firstValueFrom(this.translate.get('staffSomethingWentWrong')), 'error');
    }
  }

  // Delete-comment confirm modal state.
  showDeleteCommentModal = false;
  deletingComment = false;
  private deleteCommentTarget: ActivityComment | null = null;

  deleteComment(comment: ActivityComment) {
    if (!this.canDeleteComment(comment)) { return; }
    this.deleteCommentTarget = comment;
    this.showDeleteCommentModal = true;
  }

  closeDeleteCommentModal() {
    if (this.deletingComment) { return; }
    this.showDeleteCommentModal = false;
    this.deleteCommentTarget = null;
  }

  async confirmDeleteComment() {
    if (!this.deleteCommentTarget || this.deletingComment) { return; }
    const comment = this.deleteCommentTarget;

    const ids = this.ids();
    if (!ids) { return; }
    this.deletingComment = true;
    try {
      const response = await firstValueFrom(this.staffActivityApi.deleteComment({
        lang: 'en',
        classId: ids.classId,
        staffId: ids.staffId,
        commentId: comment.commentId,
        reason: null,
      }));
      const responseDetails = response as any;
      if (responseDetails.status) {
        this.comments = this.comments.filter(c => c.commentId !== comment.commentId);
        const post = this.posts.find(p => p.activityId === this.commentActivityId);
        if (post) {
          post.commentsCount = Math.max((post.commentsCount ?? 1) - 1, 0);
        }
      } else {
        safeToast(await firstValueFrom(this.translate.get('somethingWentWrong')), 'error');
      }
    } catch (error) {
      safeToast(await firstValueFrom(this.translate.get('staffSomethingWentWrong')), 'error');
    } finally {
      this.deletingComment = false;
      this.showDeleteCommentModal = false;
      this.deleteCommentTarget = null;
    }
  }

  // ---------- In-app media / link lightbox ----------
  // Every media type and any link found in a post opens here instead of
  // navigating away — nothing should ever leave the staff portal.
  openDocumentLightbox(url: string) {
    this.lightboxType = 'document';
    this.lightboxUrl = url;
    this.lightboxSafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    this.showLightbox = true;
  }

  openLinkLightbox(url: string) {
    this.lightboxType = 'link';
    this.lightboxUrl = url;
    this.lightboxSafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    this.showLightbox = true;
  }

  closeLightbox() {
    this.showLightbox = false;
    this.lightboxSafeUrl = null;
    this.lightboxUrl = null;
  }
}
